<template>
    <div
        :class="{
            'c-toggle': true,
            'c-toggle--on': isOn
        }"
    >
        <label
            v-if="leftLabel"
            :id="`${labelPrefix}--left`"
            :class="`c-toggle__label--left ${leftLabelClasses}`"
            @click="onClick"
        >
            {{ leftLabel }}
        </label>
        <div
            v-tab-focus.hasrole.noenter.onspace="onClick"
            class="c-toggle__container"
            :aria-labelledby="labelledBy || `${labelPrefix}--left ${labelPrefix}--right`"
            :aria-checked="`${isOn}`"
            role="switch"
        ></div>
        <label
            v-if="rightLabel"
            :id="`${labelPrefix}--right`"
            :class="`c-toggle__label--right ${rightLabelClasses}`"
            @click="onClick"
        >
            {{ rightLabel }}
        </label>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { uniqueId } from 'lodash-es';

const props = defineProps({
    isInitiallyOn: {
        type: Boolean,
        default: false,
    },
    leftLabel: {
        type: String,
        default: '',
    },
    rightLabel: {
        type: String,
        default: '',
    },
    labelledBy: {
        type: String,
        default: null,
    },
    leftLabelClasses: {
        type: String,
        default: '',
    },
    rightLabelClasses: {
        type: String,
        default: '',
    },
});

const emit = defineEmits(['input']);

// eslint-disable-next-line vue/no-setup-props-reactivity-loss
const isOn = ref(props.isInitiallyOn || false);
const labelPrefix = `c-toggle--${uniqueId()}__label`;

function onClick() {
    isOn.value = !isOn.value;
    emit('input', isOn.value);
}

/* programmatically set toggle status */
function setToggleStatus(status) {
    isOn.value = status;
}

defineExpose({
    setToggleStatus,
});
</script>

<style lang="scss">
    .c-toggle {
        $toggleWidth: 44px;
        $toggleHeight: 24px;
        $circleWidth: 20px;
        $padding: 2px;
        $this: &;

        align-items: center;
        display: flex;

        &__container {
            background-color: $nu-gray--medium;
            border-radius: 20px;
            cursor: pointer;
            height: $toggleHeight;
            position: relative;
            transition: background-color 0.2s ease;
            width: $toggleWidth;

            #{$this}--on & {
                background-color: $nu-primary;
            }

            &::after {
                background-color: $nu-white;
                border-radius: 100%;
                content: '';
                height: $circleWidth;
                left: $padding;
                top: 0;
                bottom: 0;
                margin: auto;
                position: absolute;
                width: $circleWidth;
                transition: transform 0.2s ease;

                #{$this}--on & {
                    transform: translateX($toggleWidth - $circleWidth - $padding*2)
                }
            }
        }

        &__label {
            &--left {
                margin-right: $nu-spacer-1;
            }

            &--right {
                margin-left: $nu-spacer-1;
            }
        }
    }
</style>
